/**
 * What happens after someone completes (gap map S5): the SOTG editor's survey
 * chains (`surveyActions`: `chain-survey`, `chain-releasegroup`, and
 * `chain-survey` with subType `reminderSurvey`). Each is a delay, an optional
 * hour of the day, and an optional rule on the respondent's answers.
 *
 * Pure. Staff only: clients neither see nor set them (settings.ts
 * keepStaffFields). A phased study's phases publish as chains too (`phasePlan`):
 * one SOTG survey per phase, each pushed to the previous phase's completers.
 */
import { z } from 'zod';

import { LogicGroupSchema, type BuilderDraftContent } from './draft';
import { isPhased, phaseCount, phaseQuestions } from './study';

export const CHAIN_KINDS = ['survey', 'release_group', 'reminder'] as const;
export type ChainKind = (typeof CHAIN_KINDS)[number];

export const MAX_CHAINS = 20;
/**
 * The longest wait SOTG can store: `surveyActions.delay` is minutes in a
 * smallint unsigned, about 45 days. Validation refuses more (validate.ts).
 */
export const SOTG_MAX_WAIT_MINUTES = 65_535;
/**
 * What a saved draft may hold. Kept at the 60 days Builder once allowed, so a
 * draft saved with a longer wait still opens and is told to shorten it.
 */
const MAX_DELAY_MINUTES = 60 * 24 * 60;

export const SurveyChainSchema = z
    .object({
        id: z.string().regex(/^CH\d+$/),
        kind: z.enum(CHAIN_KINDS),
        /** The SOTG survey id (survey, reminder) or release group id to add completers to. */
        target: z.string().trim().min(1).max(40),
        /** Minutes after the complete. */
        delay_minutes: z.number().int().min(0).max(MAX_DELAY_MINUTES),
        /** Send at this hour (0–23, the respondent's local time), on or after the delay. */
        at_hour: z.number().int().min(0).max(23).optional(),
        /** Only for respondents whose answers meet this rule. */
        // Lazy: draft.ts imports this module, so the schema is read once both are loaded.
        when: z.lazy(() => LogicGroupSchema).optional(),
    })
    .strict();
export type SurveyChain = z.infer<typeof SurveyChainSchema>;

/** The next free `CH<n>`. */
export function nextChainId(chains: readonly SurveyChain[]): string {
    const n = chains.reduce((m, c) => Math.max(m, Number(c.id.slice(2)) || 0), 0);
    return `CH${n + 1}`;
}

export const CHAIN_LABEL: Record<ChainKind, string> = {
    survey: 'Send another survey',
    release_group: 'Add to a release group',
    reminder: 'Send a reminder survey',
};

/** "Send survey 48214 2 days later at 9:00, if Q3 is Yes". */
export function chainLine(c: SurveyChain): string {
    const days = Math.floor(c.delay_minutes / 1440);
    const hours = Math.floor((c.delay_minutes % 1440) / 60);
    const mins = c.delay_minutes % 60;
    const delay =
        c.delay_minutes === 0
            ? 'right away'
            : `${[
                  days && `${days} day${days > 1 ? 's' : ''}`,
                  hours && `${hours} hour${hours > 1 ? 's' : ''}`,
                  mins && `${mins} minute${mins > 1 ? 's' : ''}`,
              ]
                  .filter(Boolean)
                  .join(' ')} later`;
    const what =
        c.kind === 'release_group'
            ? `Add to release group ${c.target}`
            : `${c.kind === 'reminder' ? 'Remind with' : 'Send'} survey ${c.target}`;
    return `${what} ${delay}${c.at_hour !== undefined ? ` at ${c.at_hour}:00` : ''}${c.when?.items.length ? ', if a rule holds' : ''}`;
}

export interface PhaseStep {
    phase: number;
    /** The questions this phase's SOTG survey asks. */
    questionIds: string[];
    /** Minutes after the previous phase's complete; 0 for phase 1. */
    delay_minutes: number;
}

/**
 * How a phased study publishes: one SOTG survey per phase, phase n+1 chained
 * from phase n's completers after the study's days between phases. Empty for
 * a study without phases.
 */
export function phasePlan(draft: Pick<BuilderDraftContent, 'study' | 'questions'>): PhaseStep[] {
    if (!isPhased(draft.study)) return [];
    const wait = (draft.study.days_between_phases ?? 1) * 1440;
    return Array.from({ length: phaseCount(draft) }, (_, i) => ({
        phase: i + 1,
        questionIds: phaseQuestions(draft, i + 1).map((q) => q.question_id),
        delay_minutes: i === 0 ? 0 : wait,
    }));
}
